import React, { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import BugReportIcon from '@mui/icons-material/BugReport';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import InfoIcon from '@mui/icons-material/Info';
import { KNOWN_BUGS, SYSTEM_STATUS_NOTE, getBugsByPriority, getTotalBugCount, getFixedBugCount, getBugCompletionPercentage } from '../../data/knownBugs';

interface BugsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Get the badge color for a priority level
 */
const getPriorityBadgeClass = (priority: 'high' | 'medium' | 'low') => {
  switch (priority) {
    case 'high':
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
    case 'medium':
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300';
    case 'low':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
  }
};

/**
 * Get the icon for a priority level
 */
const getPriorityIcon = (priority: 'high' | 'medium' | 'low') => {
  switch (priority) {
    case 'high':
      return <PriorityHighIcon sx={{ fontSize: 16 }} className="text-red-500 dark:text-red-400" />;
    case 'medium':
      return <BugReportIcon sx={{ fontSize: 16 }} className="text-yellow-500 dark:text-yellow-400" />;
    case 'low':
      return <BugReportIcon sx={{ fontSize: 16 }} className="text-blue-500 dark:text-blue-400" />;
  }
};

export const BugsModal: React.FC<BugsModalProps> = ({ isOpen, onClose }) => {
  const [selectedPriority, setSelectedPriority] = useState<'all' | 'high' | 'medium' | 'low'>('all');

  if (!isOpen) return null;

  const filteredBugs = selectedPriority === 'all' 
    ? KNOWN_BUGS.filter(bug => bug.status === 'pending')
    : getBugsByPriority(selectedPriority);

  const highPriorityCount = getBugsByPriority('high').length;
  const mediumPriorityCount = getBugsByPriority('medium').length;
  const lowPriorityCount = getBugsByPriority('low').length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 dark:bg-black/70"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <BugReportIcon sx={{ fontSize: 28 }} />
              Known Bugs & Issues
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {getTotalBugCount()} pending issues • {getFixedBugCount()} resolved • {getBugCompletionPercentage()}% completion rate
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Close bugs modal"
          >
            <CloseIcon sx={{ fontSize: 24 }} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <button
            onClick={() => setSelectedPriority('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedPriority === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            All ({getTotalBugCount()})
          </button>
          <button
            onClick={() => setSelectedPriority('high')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedPriority === 'high'
                ? 'bg-red-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            High Priority ({highPriorityCount})
          </button>
          <button
            onClick={() => setSelectedPriority('medium')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedPriority === 'medium'
                ? 'bg-yellow-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            Medium Priority ({mediumPriorityCount})
          </button>
          <button
            onClick={() => setSelectedPriority('low')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedPriority === 'low'
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            Low Priority ({lowPriorityCount})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* System Status Note */}
          <div className="mb-6 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 dark:border-blue-400">
            <div className="flex items-start gap-3">
              <InfoIcon className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                  Module Builder & Breakaway Hulls Status
                </h3>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  The <strong>Module Builder</strong> and <strong>Breakaway Hulls</strong> systems are currently broken and will remain broken until bugs in the Main Ship Builder are fixed first, as these systems are dependent on working code in the main Ship Builder.
                </p>
                <p className="text-sm text-blue-800 dark:text-blue-200 mt-2">
                  Bugs for those systems will be added and addressed once we can confirm that the Main Ship Builder is working to a reasonable standard.
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-2 italic">
                  Thank you for your patience and understanding.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {filteredBugs.map((bug) => (
              <div
                key={bug.id}
                className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
              >
                {/* Bug Header */}
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex-shrink-0 mt-1">
                    {getPriorityIcon(bug.priority)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                        #{bug.id}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityBadgeClass(bug.priority)}`}>
                        {bug.priority.toUpperCase()}
                      </span>
                      {bug.status === 'fixed' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                          <CheckCircleIcon sx={{ fontSize: 12 }} />
                          Fixed in {bug.version}
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                      {bug.title}
                    </h3>
                  </div>
                </div>

                {/* Bug Details */}
                <div className="space-y-2 ml-8">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Issue:
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {bug.description}
                    </p>
                  </div>

                  {bug.problem && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Problem:
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {bug.problem}
                      </p>
                    </div>
                  )}

                  {bug.impact && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Impact:
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {bug.impact}
                      </p>
                    </div>
                  )}

                  {bug.travellerRules && bug.travellerRules.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Traveller Rules:
                      </p>
                      <ul className="list-disc list-inside space-y-1 mt-1">
                        {bug.travellerRules.map((rule, index) => (
                          <li key={index} className="text-sm text-gray-600 dark:text-gray-400">
                            {rule}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {filteredBugs.length === 0 && (
              <div className="text-center py-12">
                <CheckCircleIcon sx={{ fontSize: 48 }} className="text-green-500 dark:text-green-400 mb-4 mx-auto" />
                <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  No {selectedPriority !== 'all' ? `${selectedPriority} priority ` : ''}bugs found!
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  All issues in this category have been resolved.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Found a new bug?{' '}
            <a
              href="/feedback"
              onClick={(e) => {
                e.preventDefault();
                onClose();
                window.location.href = '/feedback';
              }}
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              Report it here
            </a>
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

